import { getDoc, getDocs, onSnapshot, type DocumentData, type DocumentReference, type DocumentSnapshot, type QuerySnapshot, type Unsubscribe } from "firebase/firestore";
import { AppError, logTechnicalError, toAppError } from "@/data/errors";
import { parseAccount, parseBusiness, parseContext, parseKnowledge, parseProduct, parseScenario, parseService, parseSource } from "@/data/firestore/codec";
import { paths } from "@/data/firestore/paths";
import { validateBusinessTruth } from "@/domain/business-truth";
import type { BusinessTruth, CustomerAccount, KnowledgeItem, Product, Scenario, Service, Source } from "@/domain/types";

type Observer<T> = { next: (value: T) => void; error: (error: AppError) => void };

export async function getCurrentTruth(customerId: string): Promise<BusinessTruth> {
  try {
    const [account, business, products, services, knowledge, scenarios, sources, context] = await Promise.all([
      getDoc(paths.customer(customerId)), getDoc(paths.business(customerId)), getDocs(paths.products(customerId)), getDocs(paths.services(customerId)), getDocs(paths.knowledge(customerId)), getDocs(paths.scenarios(customerId)), getDocs(paths.sources(customerId)), getDoc(paths.context(customerId)),
    ]);
    if (!account.exists()) throw new AppError("missing-tenant", "This workspace is not configured.");
    if (!business.exists() || !context.exists()) throw new AppError("missing-document", "The Business Truth is incomplete.");
    return validateBusinessTruth({
      account: parseAccount(account.id, account.data()), business: parseBusiness(business.data()),
      products: products.docs.map(parseProduct), services: services.docs.map(parseService), knowledge: knowledge.docs.map(parseKnowledge), scenarios: scenarios.docs.map(parseScenario), sources: sources.docs.map(parseSource), context: parseContext(context.data()),
    });
  } catch (error) {
    const appError = toAppError(error, "invalid-data");
    await logTechnicalError({ customerId, operation: "get current truth", error: appError, category: "data", source: "firebase" });
    throw appError;
  }
}

type PartialTruth = { account?: CustomerAccount; business?: BusinessTruth["business"]; products?: Product[]; services?: Service[]; knowledge?: KnowledgeItem[]; scenarios?: Scenario[]; sources?: Source[]; context?: BusinessTruth["context"] };

export function subscribeCurrentTruth(customerId: string, observer: Observer<BusinessTruth>): Unsubscribe {
  const values: PartialTruth = {};
  let stopped = false;
  let failed = false;
  const fail = (error: unknown) => {
    if (stopped) return;
    const appError = toAppError(error, "subscription-failed");
    if (appError.code === "access") failed = true;
    void logTechnicalError({ customerId, operation: "subscribe current truth", error: appError, category: "data", source: "firebase" });
    observer.error(appError);
  };
  const publish = () => {
    if (stopped || failed || !values.account || !values.business || !values.products || !values.services || !values.knowledge || !values.scenarios || !values.sources || !values.context) return;
    try { observer.next(validateBusinessTruth(values)); } catch (error) { fail(error); }
  };
  const collection = <T>(snapshot: QuerySnapshot<DocumentData>, parse: (item: QuerySnapshot<DocumentData>["docs"][number]) => T) => snapshot.docs.map(parse);
  const requiredDocument = <K extends "account" | "business" | "context">(
    key: K,
    reference: DocumentReference,
    parse: (snapshot: DocumentSnapshot) => NonNullable<PartialTruth[K]>,
    missingMessage: string,
  ) => onSnapshot(reference, { includeMetadataChanges: true }, (snapshot) => {
    if (stopped) return;
    try {
      if (!snapshot.exists()) {
        delete values[key];
        // An empty cache cannot establish whether the backend document exists.
        if (!snapshot.metadata.fromCache) fail(new AppError("missing-document", missingMessage));
        return;
      }
      values[key] = parse(snapshot);
      publish();
    } catch (error) { fail(error); }
  }, fail);
  const subscriptions = [
    requiredDocument("account", paths.customer(customerId), (snapshot) => parseAccount(snapshot.id, snapshot.data()!), "This workspace is not configured."),
    requiredDocument("business", paths.business(customerId), (snapshot) => parseBusiness(snapshot.data()!), "Business profile is missing."),
    onSnapshot(paths.products(customerId), (snapshot) => { try { values.products = collection(snapshot, parseProduct); publish(); } catch (error) { fail(error); } }, fail),
    onSnapshot(paths.services(customerId), (snapshot) => { try { values.services = collection(snapshot, parseService); publish(); } catch (error) { fail(error); } }, fail),
    onSnapshot(paths.knowledge(customerId), (snapshot) => { try { values.knowledge = collection(snapshot, parseKnowledge); publish(); } catch (error) { fail(error); } }, fail),
    onSnapshot(paths.scenarios(customerId), (snapshot) => { try { values.scenarios = collection(snapshot, parseScenario); publish(); } catch (error) { fail(error); } }, fail),
    onSnapshot(paths.sources(customerId), (snapshot) => { try { values.sources = collection(snapshot, parseSource); publish(); } catch (error) { fail(error); } }, fail),
    requiredDocument("context", paths.context(customerId), (snapshot) => parseContext(snapshot.data()!), "Business Context is missing."),
  ];
  return () => { stopped = true; subscriptions.forEach((unsubscribe) => unsubscribe()); };
}
